import { useState } from "react"
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material"
import { If } from "lib/@components/switch"
import { Else } from "lib/@components/else"
import { useDialog } from "lib/@hooks/useDialog"
import { busyWhile } from "lib/@components/busy/busy-while"
import { confirm } from "lib/@dialogs/confirm"
import { successSnackbar } from "lib/snackbar/success-snackbar"
import { navigate } from "lib/routes/navigate"
import Iconify from "minimals-template/components/Iconify"
import { createSkillMap } from "routes/skill-mapper/controller/create-skill-map"
import { archiveSkillMap } from "routes/skill-mapper/controller/archive-skill-map"
import { updateSkillMap } from "routes/skill-mapper/controller/update-skill-map"
import { SkillMapRenameDialog } from "./skill-map-rename-dialog"
import { getUniqueCopyName } from "./get-unique-copy-name"
import { SkillMapArchiveMessage } from "./skill-map-archive-message"
import { restoreSkillMapWithFeedback } from "./restore-skill-map-with-feedback"

/**
 * The skill map header's actions: rename, duplicate, archive or restore. Moved out of
 * skill-map-view.js unchanged, to keep the view to one concern.
 */
export function SkillMapActionsMenu({ skillMapId: id, map, linkedRateCards }) {
    const { isArchived } = map
    const [menuAnchor, setMenuAnchor] = useState(null)
    const openRenameDialog = useDialog(<SkillMapRenameDialog />, { maxWidth: "xs", fullWidth: true })

    return (
        <>
            <IconButton
                data-cy="skill-map-menu"
                size="small"
                onClick={openMenu}
                aria-label="Skill map actions"
                // flexShrink:0 keeps the actions menu visible while the name truncates
                sx={{ flexShrink: 0 }}
            >
                <Iconify icon="eva:more-vertical-fill" />
            </IconButton>
            <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
                <If value={!isArchived}>
                    <MenuItem onClick={handleRename}>
                        <ListItemIcon>
                            <Iconify icon="solar:pen-bold" width={20} />
                        </ListItemIcon>
                        <ListItemText>Rename</ListItemText>
                    </MenuItem>
                </If>
                <MenuItem onClick={handleDuplicate}>
                    <ListItemIcon>
                        <Iconify icon="solar:copy-bold" width={20} />
                    </ListItemIcon>
                    <ListItemText>Duplicate</ListItemText>
                </MenuItem>
                <If value={!isArchived}>
                    <MenuItem onClick={handleArchive}>
                        <ListItemIcon>
                            <Iconify icon="solar:archive-bold" width={20} />
                        </ListItemIcon>
                        <ListItemText>Archive</ListItemText>
                    </MenuItem>
                    <Else>
                        <MenuItem onClick={handleRestore}>
                            <ListItemIcon>
                                <Iconify icon="solar:restart-bold" width={20} />
                            </ListItemIcon>
                            <ListItemText>Restore</ListItemText>
                        </MenuItem>
                    </Else>
                </If>
            </Menu>
        </>
    )

    function openMenu(e) {
        setMenuAnchor(e.currentTarget)
    }

    function closeMenu() {
        setMenuAnchor(null)
    }

    async function handleRename() {
        closeMenu()
        const result = await openRenameDialog({ currentId: id, name: map.name })
        if (!result) return
        const newName = result.name.trim()
        if (!newName) return
        await busyWhile(() => updateSkillMap({ id, data: { name: newName } }), "Renaming...")
        successSnackbar(`Skill map renamed to "${newName}"`)
    }

    async function handleDuplicate() {
        closeMenu()
        const newMap = await busyWhile(duplicateSkillMap, "Duplicating skill mapping...")
        if (newMap?.id) {
            navigate(`/app/optimiser/skill-mappings/${newMap.id}`)
            successSnackbar(`Skill map duplicated`)
        }
    }

    async function duplicateSkillMap() {
        const copyName = await getUniqueCopyName(map.name)
        return createSkillMap({
            name: copyName,
            startingPoint: "existing",
            sourceSkillMapId: id,
        })
    }

    async function handleArchive() {
        closeMenu()
        const ok = await confirm(
            <SkillMapArchiveMessage name={map.name} linkedRateCards={linkedRateCards} />,
            "Archive Skill Mapping"
        )
        if (!ok) return
        await busyWhile(() => archiveSkillMap({ id }), "Archiving skill mapping...")
        navigate("/app/optimiser?optimiserTab=skill-mappings")
        successSnackbar(`Skill map "${map.name}" archived`)
    }

    function handleRestore() {
        closeMenu()
        return restoreSkillMapWithFeedback({ id, name: map.name })
    }
}
